import React from 'react';
import { Award, GraduationCap, Printer, X } from 'lucide-react';
import { Button } from '../../ui/button';

/**
 * Certificat interne SADA imprimable (HTML avec bouton d'impression).
 * L'utilisateur peut Ctrl+P → PDF depuis son navigateur.
 */
const AcademyInternalCertificate = ({ data, onClose }) => {
  if (!data) return null;
  const {
    certificate,
    agent_name,
    agent_poste,
    training_title,
    training_category,
    validated_at,
    validated_by_name,
    expires_at,
    quiz_score_pct,
    practical_score,
    practical_max,
  } = data;

  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—');
  const posteLabels = { auxiliaire: 'Auxiliaire', pharmacien: 'Pharmacien', administratif: 'Administratif' };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 print:bg-white print:p-0" data-testid="academy-internal-cert">
      <div className="max-w-3xl w-full max-h-[95vh] overflow-y-auto rounded-2xl bg-white shadow-2xl print:shadow-none print:rounded-none print:max-h-none">
        {/* Top actions (hidden in print) */}
        <div className="flex justify-end gap-2 p-3 print:hidden">
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="academy-cert-print-btn">
            <Printer className="w-4 h-4 mr-1" /> Imprimer / Enregistrer PDF
          </Button>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="academy-cert-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-8 sm:p-12 border-4 border-emerald-600 m-4 print:m-6 print:border-emerald-700">
          {/* En-tête */}
          <div className="text-center border-b-2 border-emerald-200 pb-4 mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <GraduationCap className="w-10 h-10 text-emerald-600" />
              <div className="text-left">
                <div className="text-2xl font-serif font-bold text-emerald-800 leading-none">SADA Academy</div>
                <div className="text-xs text-slate-600 tracking-wider">PHARMACIE SAINT ANGE DES AMIS · COCODY</div>
              </div>
            </div>
            <div className="mt-4 text-4xl font-serif font-bold text-slate-900 uppercase tracking-wide">
              Certificat de réussite
            </div>
            <div className="text-sm text-slate-500 mt-1">Micro-certification interne</div>
          </div>

          {/* Corps */}
          <div className="text-center space-y-4">
            <div className="text-slate-600 text-sm">Est délivré à</div>
            <div className="text-3xl font-serif font-bold text-slate-900" data-testid="cert-agent-name">{agent_name || '—'}</div>
            {agent_poste && (
              <div className="text-slate-500 text-sm">Poste : {posteLabels[agent_poste] || agent_poste}</div>
            )}
            <div className="text-slate-600 text-sm mt-6">pour avoir validé avec succès la formation</div>
            <div className="text-xl font-semibold text-emerald-700 max-w-2xl mx-auto" data-testid="cert-training-title">
              « {training_title} »
            </div>
            {training_category && (
              <div className="text-xs text-slate-500 italic">{training_category}</div>
            )}
          </div>

          {/* Scores */}
          <div className="mt-8 grid grid-cols-2 gap-4 max-w-md mx-auto">
            <div className="bg-emerald-50 rounded-lg p-3 text-center border border-emerald-200">
              <div className="text-xs text-emerald-800 uppercase tracking-wide">Quiz</div>
              <div className="text-2xl font-bold text-emerald-700">{quiz_score_pct ?? 0} %</div>
              <div className="text-xs text-slate-500">Seuil : 80 %</div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3 text-center border border-emerald-200">
              <div className="text-xs text-emerald-800 uppercase tracking-wide">Évaluation pratique</div>
              <div className="text-2xl font-bold text-emerald-700">{practical_score ?? 0} / {practical_max ?? 20}</div>
              <div className="text-xs text-slate-500">Seuil : 16 / 20</div>
            </div>
          </div>

          {/* Métadonnées */}
          <div className="mt-8 pt-6 border-t border-slate-200 grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-slate-500">Date de délivrance</div>
              <div className="font-medium text-slate-900">{fmt(validated_at)}</div>
            </div>
            <div>
              <div className="text-slate-500">Valable jusqu&apos;au</div>
              <div className="font-medium text-slate-900">{fmt(expires_at)}</div>
            </div>
            <div>
              <div className="text-slate-500">Validé par</div>
              <div className="font-medium text-slate-900">{validated_by_name || '—'}</div>
            </div>
            <div>
              <div className="text-slate-500">Numéro de certificat</div>
              <div className="font-mono text-slate-900 flex items-center gap-1">
                <Award className="w-3 h-3 text-amber-500" />
                {certificate?.number}
              </div>
            </div>
          </div>

          <div className="text-center text-xs text-slate-400 mt-6 italic">
            Ce certificat est délivré à titre interne par la Pharmacie SADA. Renouvellement annuel requis.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcademyInternalCertificate;
